import type { CodeableConcept } from "@medplum/fhirtypes";

/**
 * The shape a coded value takes while it lives in FORM state.
 *
 * Deliberately flat, and deliberately not `CodeableConcept`. TanStack Form's
 * `DeepKeysAndValuesImpl` walks every property of the form's value type with no
 * depth limit, and FHIR `CodeableConcept` reaches `Extension`, which contains
 * `Extension[]` — recursive, with ~50 `value[x]` members at each level. A single
 * component holding a `CodeableConcept` field put `tsc` past an 8 GB heap.
 *
 * This is also the shape the read model already speaks: `ConditionDto.code` and
 * `SymptomDto.code` are flattened exactly like this. Rehydrate at the submit
 * boundary with `toCodeableConcept`, which is the only place FHIR shape is
 * needed.
 */
export interface CodeValue {
  system?: string;
  code?: string;
  display?: string;
  text?: string;
}

/** A code has been chosen when it carries an actual code. */
export const hasCode = (v: CodeValue | undefined | null): boolean => Boolean(v?.code);

/** Form state → FHIR, at the write boundary. */
export function toCodeableConcept(v: CodeValue): CodeableConcept {
  const coding = v.system || v.code || v.display ? [{ system: v.system, code: v.code, display: v.display }] : undefined;
  return { ...(coding ? { coding } : {}), ...(v.text ? { text: v.text } : {}) };
}

/** FHIR → form state, when seeding a form from a resource rather than a DTO. */
export function fromCodeableConcept(c: CodeableConcept | undefined): CodeValue | undefined {
  if (!c) return undefined;
  const coding = c.coding?.[0];
  return {
    ...(coding?.system ? { system: coding.system } : {}),
    ...(coding?.code ? { code: coding.code } : {}),
    ...(coding?.display ? { display: coding.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
}
